<template>
    <section :class="['focused-dashboard-shell', { dark: isDark }]">
        <div class="focused-dashboard-header">
            <div>
                <p class="dashboard-eyebrow">Focused Dashboard</p>
                <h1>{{ dashboard.title }}</h1>
                <p class="dashboard-summary">{{ dashboard.summary }}</p>
            </div>
            <div class="dashboard-header-actions">
                <span :class="['status-pill', dashboard.status]">{{ formatStatus(dashboard.status) }}</span>
                <router-link
                    v-if="primaryReportLink"
                    :to="primaryReportLink.route"
                    class="dashboard-link"
                >
                    {{ primaryReportLink.label }}
                    <i class="material-icons">arrow_forward</i>
                </router-link>
            </div>
        </div>

        <AnalyticsGoalPanel :is-dark="isDark" :active-goal-id="dashboard.id" />

        <div class="readiness-grid">
            <article v-for="item in readinessCards" :key="item.title" class="readiness-card">
                <div class="readiness-icon">
                    <i class="material-icons">{{ item.icon }}</i>
                </div>
                <div>
                    <p>{{ item.label }}</p>
                    <h2>{{ item.title }}</h2>
                    <span>{{ item.description }}</span>
                </div>
            </article>
        </div>

        <section v-if="dashboard.status !== 'deferred'" class="dashboard-section dashboard-workspace">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Dashboard Buildout</p>
                    <h2>{{ dashboard.title }} working view</h2>
                </div>
                <span class="section-count">{{ dashboardBlueprint.label }}</span>
            </div>

            <div class="signal-grid">
                <article
                    v-for="signal in dashboardBlueprint.kpis"
                    :key="signal.label"
                    :class="['signal-card', signal.tone]"
                >
                    <div class="signal-icon">
                        <i class="material-icons">{{ signal.icon }}</i>
                    </div>
                    <div>
                        <p>{{ signal.label }}</p>
                        <h3>{{ signal.value }}</h3>
                        <span>{{ signal.helper }}</span>
                    </div>
                </article>
            </div>

            <div class="dashboard-board">
                <article
                    v-for="chart in dashboardBlueprint.charts"
                    :key="chart.title"
                    class="visual-card"
                >
                    <div class="visual-card-header">
                        <div>
                            <h3>{{ chart.title }}</h3>
                            <p>{{ chart.subtitle }}</p>
                        </div>
                        <span>{{ chart.seriesName }}</span>
                    </div>
                    <div class="visual-frame">
                        <EChartsLineChart
                            v-if="chart.type === 'line'"
                            :labels="chart.labels"
                            :values="chart.values"
                            :series-name="chart.seriesName"
                            :aria-label="`${dashboard.title} ${chart.title}`"
                            :is-dark="isDark"
                            :value-formatter="chartFormatter(chart.valueType)"
                            :axis-value-formatter="chartAxisFormatter(chart.valueType)"
                        />
                        <EChartsHorizontalBarChart
                            v-else
                            :categories="chart.categories"
                            :values="chart.values"
                            :series-name="chart.seriesName"
                            :aria-label="`${dashboard.title} ${chart.title}`"
                            :is-dark="isDark"
                            :value-formatter="chartFormatter(chart.valueType)"
                        />
                    </div>
                </article>

                <article class="work-queue-card">
                    <div class="visual-card-header">
                        <div>
                            <h3>Build Queue</h3>
                            <p>Next source and product decisions for this page</p>
                        </div>
                    </div>
                    <ol class="queue-list">
                        <li v-for="item in dashboardBlueprint.queue" :key="item.title">
                            <span :class="['queue-severity', item.severity]">{{ item.severity }}</span>
                            <strong>{{ item.title }}</strong>
                            <p>{{ item.description }}</p>
                        </li>
                    </ol>
                </article>
            </div>

            <div class="source-health-grid">
                <article
                    v-for="source in dashboardBlueprint.sourceHealth"
                    :key="source.label"
                    :class="['source-health-card', source.status]"
                >
                    <span>{{ source.label }}</span>
                    <strong>{{ formatHealthStatus(source.status) }}</strong>
                </article>
            </div>
        </section>

        <section v-else class="dashboard-section deferred-dashboard-notice">
            <i class="material-icons" aria-hidden="true">pause_circle</i>
            <div>
                <p class="dashboard-eyebrow">Deferred by decision</p>
                <h2>No active build queue</h2>
                <p>This dashboard will remain out of the active analytics program until its transaction channel and measurement contract are ready.</p>
            </div>
        </section>

        <section v-if="dashboard.status !== 'deferred'" class="dashboard-section">
            <div class="section-header">
                <div>
                    <p class="dashboard-eyebrow">Metric Plan</p>
                    <h2>Core metrics for this dashboard</h2>
                </div>
                <span class="section-count">{{ dashboard.metrics.length }} metrics</span>
            </div>
            <div class="metric-grid">
                <article v-for="metric in metricCards" :key="metric.title" class="metric-plan-card">
                    <div class="metric-card-header">
                        <h3>{{ metric.title }}</h3>
                        <span :class="['metric-status', metric.status]">{{ metric.statusLabel }}</span>
                    </div>
                    <p>{{ metric.description }}</p>
                </article>
            </div>
        </section>

        <div class="dashboard-detail-grid">
            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Source Requirements</p>
                        <h2>Data needed before production decisions</h2>
                    </div>
                </div>
                <ul class="source-list">
                    <li v-for="requirement in dashboard.sourceRequirements" :key="requirement">
                        <i class="material-icons">rule</i>
                        <span>{{ requirement }}</span>
                    </li>
                </ul>
            </section>

            <section class="dashboard-section">
                <div class="section-header">
                    <div>
                        <p class="dashboard-eyebrow">Drill-through</p>
                        <h2>Existing operational pages</h2>
                    </div>
                </div>
                <div class="report-links">
                    <router-link
                        v-for="link in dashboard.reportLinks"
                        :key="link.route"
                        :to="link.route"
                        class="report-row"
                    >
                        <span>{{ link.label }}</span>
                        <i class="material-icons">arrow_forward</i>
                    </router-link>
                </div>
            </section>
        </div>
    </section>
</template>

<script>
import AnalyticsGoalPanel from "./AnalyticsGoalPanel.vue"
import EChartsHorizontalBarChart from "../charts/EChartsHorizontalBarChart.vue"
import EChartsLineChart from "../charts/EChartsLineChart.vue"
import { getAnalyticsDashboardById } from "../../utils/analytics-dashboard-goals"

const currencyFormatter = new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
})

const compactNumberFormatter = new Intl.NumberFormat("en-CA", {
    notation: "compact",
    maximumFractionDigits: 1,
})

const metricDescriptions = {
    "Low stock": "Shows inventory below operating thresholds by store so replenishment work can start before shelves are empty.",
    "Stockout risk": "Combines current stock with recent velocity to flag items likely to run out soon.",
    "Inventory aging": "Separates inventory by days since receipt or last movement so older stock is visible.",
    "Sell-through": "Tracks how quickly received inventory turns into sales over a selected window.",
    "Stale inventory": "Highlights products with stock on hand but weak or no recent movement.",
    "Transfer aging": "Flags transfers that are open, delayed, or waiting on receiving action.",
    "Receiving exceptions": "Captures purchase order receipts with missing, mismatched, or delayed quantities.",
    "Adjustment volume": "Shows quantity and price adjustments by reason, store, and trend.",
    "Inventory value at risk": "Estimates dollars tied up in low-velocity, aging, or high-risk stock.",
    "Top movers": "Ranks products with the strongest unit or revenue movement in the selected period.",
    "Slow movers": "Ranks products with poor movement relative to stock on hand and expected velocity.",
    "Category share": "Shows how product categories contribute to revenue, units, and mix over time.",
    "Brand/vendor performance": "Compares brands and suppliers by sales, velocity, and inventory contribution.",
    "Price-band performance": "Groups products by price range so pricing concentration and demand can be reviewed.",
    "Margin scatter": "Compares margin and sales volume once cost data is reliable enough to trust.",
    "Product attach behavior": "Shows which products commonly appear together in the same basket.",
    "AGCO status by store": "Summarizes current compliance submission health across all stores.",
    "AGCO timeline": "Shows submission status changes over time so recurring issues are visible.",
    "Failure reasons": "Groups failed submissions by error reason to focus remediation.",
    "Overdue reports": "Flags stores with reports outside the required reporting window.",
    "Unknown warehouse cleanup": "Measures sales assigned to unknown warehouse and routes cleanup work.",
    "Test data detection": "Highlights test-named products, customers, or invoices leaking into reporting.",
    "Import/sync health": "Summarizes import jobs, sync failures, and stale integration data.",
    "New vs returning customers": "Separates first-time buyers from returning customers over the selected period.",
    "Repeat purchase rate": "Shows what share of customers come back after their first purchase.",
    "Purchase frequency": "Measures how often customers buy within a rolling period.",
    "Cohort retention": "Tracks customer retention by first purchase cohort.",
    "Loyalty opt-ins": "Shows loyalty adoption and consent status by store or channel.",
    "Campaign revenue": "Attributes revenue and orders to loyalty or engagement campaigns.",
    "Winback lists": "Identifies customers who are eligible for retention outreach.",
    "Customer value bands": "Groups customers by spend, visits, or projected value.",
    "Online revenue": "Shows ecommerce sales once the online store is active.",
    "Online vs in-store channel mix": "Compares ecommerce and retail POS contribution across the same dates.",
    "Conversion funnel": "Tracks storefront sessions through cart, checkout, payment, and order completion.",
    "Abandoned carts": "Measures carts that were started but not converted into orders.",
    "Fulfillment queue": "Shows pickup and delivery orders waiting for action.",
    "Pickup/delivery SLA": "Measures whether ecommerce orders are fulfilled within promised windows.",
    "Failed payments": "Surfaces payment attempts that fail during online checkout.",
    "Checkout errors": "Groups checkout failures by error type and step.",
    "Inventory sync health": "Shows whether online availability matches POS inventory.",
    "Missing images": "Flags products that are not ready for ecommerce merchandising.",
    "Unpublished products": "Shows catalog items that are excluded from the storefront.",
    "Net revenue bridge": "Connects gross sales, discounts, refunds, tax, and net revenue in one view.",
    "Tax collected": "Summarizes tax totals by store, period, and reportable category.",
    "Cash drawer variance": "Highlights expected versus counted drawer differences.",
    "Safe activity": "Shows safe drops, pulls, transfers, and balance movement.",
    "Gross margin": "Adds profitability once cost data has clear ownership and quality checks.",
    "Forecast vs actual": "Compares actual results to store targets or forecasts.",
    "Multi-store scorecard": "Ranks stores across revenue, inventory, compliance, and finance indicators.",
}

export default {
    name: "FocusedDashboardShell",
    components: {
        AnalyticsGoalPanel,
        EChartsHorizontalBarChart,
        EChartsLineChart,
    },
    props: {
        goalId: {
            type: String,
            required: true,
        },
        isDark: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        dashboard() {
            return getAnalyticsDashboardById(this.goalId) || {
                id: this.goalId,
                title: "Dashboard",
                status: "planned",
                summary: "This dashboard has not been configured yet.",
                primarySource: "Source pending.",
                metrics: [],
                blueprint: null,
                sourceRequirements: [],
                reportLinks: [],
            }
        },
        dashboardBlueprint() {
            return this.dashboard.blueprint || {
                label: "Setup preview",
                kpis: [],
                charts: [],
                queue: [],
                sourceHealth: [],
            }
        },
        primaryReportLink() {
            return this.dashboard.reportLinks && this.dashboard.reportLinks[0]
        },
        readinessCards() {
            return [
                {
                    icon: "schema",
                    label: "Primary source",
                    title: "Source contract needed",
                    description: this.dashboard.primarySource,
                },
                {
                    icon: "verified",
                    label: "Decision status",
                    title: this.decisionStatus.title,
                    description: this.decisionStatus.description,
                },
                {
                    icon: "query_stats",
                    label: "Build shape",
                    title: "Charts plus drill-through",
                    description: "Top-level charts should link to detailed reports, cleanup queues, or operational records.",
                },
            ]
        },
        metricCards() {
            return this.dashboard.metrics.map((metric, index) => ({
                title: metric,
                status: index < 3 ? "next" : "planned",
                statusLabel: index < 3 ? "Next" : "Planned",
                description: metricDescriptions[metric] || "Define the calculation, filters, source table, and owner before this metric is treated as live.",
            }))
        },
        decisionStatus() {
            if (this.dashboard.status === "in_progress") {
                return {
                    title: "In progress",
                    description: "Initial dashboard surface exists; remaining metrics wait on source contracts.",
                }
            }
            if (this.dashboard.status === "deferred") {
                return {
                    title: "Deferred",
                    description: "This dashboard is intentionally outside the active analytics program.",
                }
            }
            return {
                title: "Planned",
                description: "Do not treat these metrics as production decision surfaces until setup is complete.",
            }
        },
    },
    methods: {
        formatStatus(status) {
            return String(status || "").replace(/_/g, " ")
        },
        formatHealthStatus(status) {
            return String(status || "").replace(/_/g, " ")
        },
        chartFormatter(valueType) {
            return value => this.formatChartValue(value, valueType)
        },
        chartAxisFormatter(valueType) {
            return value => this.formatChartAxisValue(value, valueType)
        },
        formatChartValue(value, valueType) {
            const numericValue = Number(value) || 0

            if (valueType === "currency") {
                return currencyFormatter.format(numericValue)
            }

            if (valueType === "percent") {
                return `${Math.round(numericValue)}%`
            }

            if (valueType === "days") {
                return `${Math.round(numericValue)}d`
            }

            return compactNumberFormatter.format(numericValue)
        },
        formatChartAxisValue(value, valueType) {
            const numericValue = Number(value) || 0

            if (valueType === "currency") {
                return currencyFormatter.format(numericValue)
            }

            if (valueType === "percent") {
                return `${Math.round(numericValue)}%`
            }

            if (valueType === "days") {
                return `${Math.round(numericValue)}d`
            }

            return compactNumberFormatter.format(numericValue)
        },
    },
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "@/assets/variables.scss" as *;

.focused-dashboard-shell {
    color: $textColor;
}

.focused-dashboard-header {
    align-items: flex-start;
    display: flex;
    gap: 18px;
    justify-content: space-between;
    margin-bottom: 16px;

    h1 {
        color: $primaryColor;
        font-size: 24px;
        font-weight: 800;
        line-height: 1.2;
        margin: 0;
    }
}

.dashboard-summary {
    color: rgba($textColor, 0.68);
    font-size: 13px;
    line-height: 1.5;
    margin: 8px 0 0;
    max-width: 760px;
}

.dashboard-eyebrow {
    color: rgba($textColor, 0.56);
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    margin: 0 0 4px;
    text-transform: uppercase;
}

.dashboard-header-actions {
    align-items: flex-end;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.dashboard-link {
    align-items: center;
    color: $primaryColor;
    display: inline-flex;
    font-size: 13px;
    font-weight: 800;
    gap: 6px;
    text-decoration: none;
    white-space: nowrap;

    i {
        font-size: 18px;
    }

    &:hover {
        color: $secondaryColor;
        text-decoration: none;
    }
}

.status-pill,
.section-count,
.metric-status {
    border-radius: 999px;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    padding: 6px 9px;
    text-transform: uppercase;
    white-space: nowrap;
}

.status-pill {
    &.in_progress {
        background: rgba($successColor, 0.14);
        color: color.adjust($successColor, $lightness: -14%);
    }

    &.planned {
        background: rgba($warningColor, 0.14);
        color: color.adjust($warningColor, $lightness: -24%);
    }

    &.deferred {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }
}

.readiness-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    margin: 0 0 18px;
}

.readiness-card,
.dashboard-section {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    box-shadow: $cardShadow;
}

.readiness-card {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    padding: 16px;

    p {
        color: rgba($textColor, 0.58);
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0;
        margin: 0 0 5px;
        text-transform: uppercase;
    }

    h2 {
        color: $primaryColor;
        font-size: 16px;
        font-weight: 800;
        margin: 0 0 6px;
    }

    span {
        color: rgba($textColor, 0.68);
        display: block;
        font-size: 12px;
        line-height: 1.45;
    }
}

.deferred-dashboard-notice {
    align-items: flex-start;
    display: flex;
    gap: 14px;
    padding: 20px;

    > i {
        color: rgba($textColor, 0.52);
        font-size: 28px;
    }

    h2 {
        color: $primaryColor;
        font-size: 18px;
        margin: 0 0 6px;
    }

    p:not(.dashboard-eyebrow) {
        color: rgba($textColor, 0.66);
        font-size: 12px;
        line-height: 1.5;
        margin: 0;
    }
}

.dark .deferred-dashboard-notice {
    > i { color: rgba($darkTextColor, 0.58); }
    h2 { color: $secondaryColorDark; }
    p:not(.dashboard-eyebrow) { color: rgba($darkTextColor, 0.68); }
}

.readiness-icon {
    align-items: center;
    background: rgba($primaryColor, 0.08);
    border-radius: 8px;
    color: $primaryColor;
    display: flex;
    flex: 0 0 36px;
    height: 36px;
    justify-content: center;
    width: 36px;

    i {
        font-size: 20px;
    }
}

.dashboard-section {
    margin-bottom: 18px;
    padding: 16px;
}

.dashboard-workspace {
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;

    .section-count {
        line-height: 1.25;
        max-width: 320px;
        text-align: right;
        white-space: normal;
    }
}

.section-header {
    align-items: flex-start;
    display: flex;
    gap: 14px;
    justify-content: space-between;
    margin-bottom: 14px;

    h2 {
        color: $primaryColor;
        font-size: 17px;
        font-weight: 800;
        margin: 0;
    }
}

.section-count {
    background: rgba($primaryColor, 0.08);
    color: $primaryColor;
}

.signal-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-bottom: 12px;
}

.signal-card {
    align-items: flex-start;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-left: 3px solid rgba($primaryColor, 0.3);
    border-radius: 8px;
    box-shadow: $cardShadow;
    display: flex;
    gap: 12px;
    min-height: 116px;
    padding: 14px;

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        font-weight: 700;
        margin: 0 0 7px;
    }

    h3 {
        color: $primaryColor;
        font-size: 23px;
        font-weight: 800;
        line-height: 1.05;
        margin: 0;
    }

    span {
        color: rgba($textColor, 0.62);
        display: block;
        font-size: 11px;
        line-height: 1.35;
        margin-top: 8px;
    }

    &.success {
        border-left-color: $successColor;
    }

    &.warning {
        border-left-color: $warningColor;
    }

    &.danger {
        border-left-color: $dangerColor;
    }
}

.signal-icon {
    align-items: center;
    background: rgba($primaryColor, 0.08);
    border-radius: 8px;
    color: $primaryColor;
    display: flex;
    flex: 0 0 34px;
    height: 34px;
    justify-content: center;
    width: 34px;

    i {
        font-size: 19px;
    }
}

.dashboard-board {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(300px, 0.72fr);
    margin-bottom: 12px;
}

.visual-card,
.work-queue-card {
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    box-shadow: $cardShadow;
    min-width: 0;
    padding: 14px;
}

.visual-card-header {
    align-items: flex-start;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 10px;

    h3 {
        color: $primaryColor;
        font-size: 15px;
        font-weight: 800;
        margin: 0 0 4px;
    }

    p {
        color: rgba($textColor, 0.62);
        font-size: 12px;
        line-height: 1.35;
        margin: 0;
    }

    span {
        background: rgba($primaryColor, 0.08);
        border-radius: 999px;
        color: $primaryColor;
        flex: 0 0 auto;
        font-size: 10px;
        font-weight: 800;
        line-height: 1;
        padding: 6px 8px;
        text-transform: uppercase;
    }
}

.visual-frame {
    height: 286px;
    min-width: 0;

    ::v-deep .echarts-line-chart,
    ::v-deep .echarts-horizontal-bar-chart {
        min-height: 286px;
    }
}

.queue-list {
    counter-reset: queue-item;
    display: grid;
    gap: 10px;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
        border-top: 1px solid $lightBorderColor;
        padding-top: 10px;

        &:first-child {
            border-top: 0;
            padding-top: 0;
        }
    }

    strong {
        color: $primaryColor;
        display: block;
        font-size: 13px;
        line-height: 1.25;
        margin: 6px 0 4px;
    }

    p {
        color: rgba($textColor, 0.64);
        font-size: 12px;
        line-height: 1.4;
        margin: 0;
    }
}

.queue-severity {
    border-radius: 999px;
    display: inline-flex;
    font-size: 10px;
    font-weight: 800;
    line-height: 1;
    padding: 5px 7px;
    text-transform: uppercase;

    &.next {
        background: rgba($successColor, 0.14);
        color: color.adjust($successColor, $lightness: -14%);
    }

    &.planned {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }
}

.source-health-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.source-health-card {
    align-items: center;
    background: $lightCardBg;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    display: flex;
    gap: 10px;
    justify-content: space-between;
    padding: 11px 12px;

    span {
        color: rgba($textColor, 0.68);
        font-size: 12px;
        font-weight: 700;
    }

    strong {
        border-radius: 999px;
        font-size: 10px;
        font-weight: 800;
        line-height: 1;
        padding: 5px 7px;
        text-transform: uppercase;
    }

    &.available strong {
        background: rgba($successColor, 0.14);
        color: color.adjust($successColor, $lightness: -14%);
    }

    &.partial strong {
        background: rgba($warningColor, 0.14);
        color: color.adjust($warningColor, $lightness: -24%);
    }

    &.needed strong {
        background: rgba($dangerColor, 0.12);
        color: color.adjust($dangerColor, $lightness: -10%);
    }
}

.metric-grid {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.metric-plan-card {
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    min-height: 116px;
    padding: 12px;

    h3 {
        color: $primaryColor;
        font-size: 14px;
        font-weight: 800;
        line-height: 1.25;
        margin: 0;
    }

    p {
        color: rgba($textColor, 0.66);
        font-size: 12px;
        line-height: 1.45;
        margin: 9px 0 0;
    }
}

.metric-card-header {
    align-items: flex-start;
    display: flex;
    gap: 10px;
    justify-content: space-between;
}

.metric-status {
    &.next {
        background: rgba($successColor, 0.14);
        color: color.adjust($successColor, $lightness: -14%);
    }

    &.planned {
        background: rgba($textColor, 0.08);
        color: rgba($textColor, 0.62);
    }
}

.dashboard-detail-grid {
    display: grid;
    gap: 12px;
    grid-template-columns: minmax(0, 1fr) minmax(320px, 0.55fr);
}

.source-list {
    display: grid;
    gap: 10px;
    list-style: none;
    margin: 0;
    padding: 0;

    li {
        align-items: flex-start;
        border: 1px solid $lightBorderColor;
        border-radius: 8px;
        display: flex;
        gap: 10px;
        padding: 10px;
    }

    i {
        color: $warningColor;
        font-size: 18px;
        margin-top: 1px;
    }

    span {
        color: rgba($textColor, 0.68);
        font-size: 12px;
        line-height: 1.45;
    }
}

.report-links {
    display: grid;
    gap: 8px;
}

.report-row {
    align-items: center;
    border: 1px solid $lightBorderColor;
    border-radius: 8px;
    color: $primaryColor;
    display: flex;
    font-size: 13px;
    font-weight: 800;
    justify-content: space-between;
    padding: 12px;
    text-decoration: none;

    i {
        font-size: 18px;
    }

    &:hover {
        border-color: rgba($primaryColor, 0.34);
        color: $secondaryColor;
        text-decoration: none;
    }
}

.dark {
    color: $darkTextColor;

    .focused-dashboard-header h1,
    .section-header h2,
    .metric-plan-card h3,
    .readiness-card h2,
    .signal-card h3,
    .visual-card-header h3,
    .queue-list strong {
        color: $secondaryColorDark;
    }

    .dashboard-summary,
    .readiness-card span,
    .metric-plan-card p,
    .source-list span,
    .signal-card span,
    .visual-card-header p,
    .queue-list p,
    .source-health-card span {
        color: rgba($darkTextColor, 0.72);
    }

    .dashboard-eyebrow,
    .readiness-card p {
        color: rgba($darkTextColor, 0.62);
    }

    .readiness-card,
    .dashboard-section,
    .signal-card,
    .visual-card,
    .work-queue-card,
    .source-health-card {
        background: $darkCardBg;
        border-color: $darkBorderColor;
    }

    .dashboard-workspace {
        background: transparent;
        border: 0;
    }

    .readiness-icon,
    .section-count,
    .signal-icon,
    .visual-card-header span {
        background: rgba($secondaryColorDark, 0.12);
        color: $secondaryColorDark;
    }

    .metric-plan-card,
    .source-list li,
    .report-row,
    .queue-list li {
        border-color: $darkBorderColor;
    }

    .report-row,
    .dashboard-link {
        color: $secondaryColorDark;
    }

    .metric-status.planned {
        background: rgba($darkTextColor, 0.1);
        color: rgba($darkTextColor, 0.66);
    }

    .status-pill.deferred {
        background: rgba($darkTextColor, 0.1);
        color: rgba($darkTextColor, 0.66);
    }

    .queue-severity.planned {
        background: rgba($darkTextColor, 0.1);
        color: rgba($darkTextColor, 0.66);
    }
}

@media (max-width: 1024px) {
    .readiness-grid,
    .dashboard-detail-grid,
    .dashboard-board {
        grid-template-columns: 1fr;
    }

    .signal-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 768px) {
    .focused-dashboard-header,
    .section-header {
        align-items: stretch;
        flex-direction: column;
    }

    .dashboard-header-actions {
        align-items: flex-start;
    }

    .metric-grid {
        grid-template-columns: 1fr;
    }

    .dashboard-workspace .section-count {
        max-width: none;
        text-align: left;
    }

    .signal-grid,
    .source-health-grid {
        grid-template-columns: 1fr;
    }

    .visual-card-header {
        flex-direction: column;
    }
}
</style>
